/*
 *  @providesModule DepositOfferTile
 *
 */

/* eslint-disable no-magic-numbers */

import * as Animatable from 'react-native-animatable';
import I18n from 'INTL';
import Label from 'Label';
import LinearGradient from 'react-native-linear-gradient';
import PropTypes from 'prop-types';
import React from 'react';
import SheenEffect from 'SheenEffect';
import Sizes from 'Sizes';
import SkillzImage from 'SkillzImage';
import SkillzStyle from 'SkillzStyle';
import styles from 'DepositOfferTileStyles';

import {Platform, TouchableOpacity, View} from 'react-native';

const {btnGradientStore} = SkillzStyle.fullStyles;

const BOX_HEIGHT = Sizes.dynamic({
  phone: 80,
  tablet: 100,
});

const PROMO_BUMP_ANIMATION_DURATION = Platform.select({ios: 420, android: 400, web: 420});
const PROMO_BUMP_ANIMATION_UPDATE_OFFSET = Platform.select({ios: 60, android: 50, web: 60});
const BADGE_ANIMATION_DURATION = 500;
const BADGE_ANIMATION_DELAY = 1000;

const TEST_IDS = {
  DEPOSIT_OFFER_TILE_BEST_VALUE_BAGDE: 'deposit_offer_tile_best_value_badge',
};

export default class DepositOfferTile extends React.Component {
  static propTypes = {
    onChange: PropTypes.func,
    offerId: PropTypes.any,
    isBestOffer: PropTypes.bool,
    isExtendedWidth: PropTypes.bool,
    titleText: PropTypes.string,
    primaryText: PropTypes.string,
    primaryTextLine2: PropTypes.string,
    subText: PropTypes.string,
    isSelected: PropTypes.bool,
    isExtraLarge: PropTypes.bool,
    width: PropTypes.number,
    primaryTextStyle: PropTypes.object,
    index: PropTypes.number,
    touchOpacity: PropTypes.number,
    style: PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.number]),
  };

  constructor(props: unknown) {
    super(props);

    this.state = {
      displayedSubText: this.props.subText,
    };
  }

  componentDidUpdate(prevProps: Object) {
    if (this.props.subText !== prevProps.subText) {
      this.depositButton?.pulse?.(PROMO_BUMP_ANIMATION_DURATION);
      setTimeout(() => {
        this.setState({
          displayedSubText: this.props.subText,
        });
      }, PROMO_BUMP_ANIMATION_UPDATE_OFFSET);
    }
  }

  _renderSmallContentBox(): React.ReactNode {
    return (
      <View style={{flex: 1, alignItems: 'center', flexDirection: 'column'}}>
        {this._renderTopSection()}
        {this._renderHR()}
        {this._renderBottomSection()}
      </View>
    );
  }

  _renderTitle(): React.ReactNode | null {
    let fontSize = BOX_HEIGHT * (this.props.primaryTextLine2 ? 0.325 : 0.525);
    if (this.props.primaryText.length >= 6) {
      fontSize *= 0.9;
    }
    let titleText = false;
    if (this.props.titleText) {
      const titleFontSize = fontSize * 0.3;
      fontSize = fontSize * 0.6;
      titleText = (
        <Label
          style={[styles.titleStyle, {fontSize: titleFontSize}]}
          key={this.props.titleText}
          accessible={false}
        >
          {this.props.titleText}
        </Label>
      );
    }

    const noTitleStyle = this.props.titleText ? {marginTop: -2} : {marginTop: 0};
    const primaryText = (
      <Label
        style={[styles.titleStyle, {fontSize: fontSize}, noTitleStyle]}
        key={this.props.primaryText}
        accessible={false}
      >
        {this.props.primaryText}
      </Label>
    );

    let primaryTextLine2 = false;
    if (this.props.primaryTextLine2) {
      primaryTextLine2 = (
        <Label
          style={[styles.titleStyle, {fontSize: fontSize}, noTitleStyle]}
          key={this.props.primaryTextLine2}
          accessible={false}
        >
          {this.props.primaryTextLine2}
        </Label>
      );
    }

    if (this.props.titleText) {
      return (
        <View style={{flexShrink: 1}}>
          {titleText}
          {primaryText}
        </View>
      );
    } else if (this.props.primaryTextLine2) {
      return (
        <View style={{flexShrink: 1}}>
          {primaryText}
          {primaryTextLine2}
        </View>
      );
    }
    return <View style={{flexShrink: 1}}>{primaryText}</View>;
  }

  _renderCheck(): React.ReactNode | null {
    if (this.props.titleText && this.props.isSelected) {
      return (
        <View style={styles.checkContainer}>
          <SkillzImage
            style={styles.check}
            source={'ic_check_icon_skz.png'}
            key={this.props.offerId}
          />
        </View>
      );
    }
    return null;
  }

  _renderTopSection(): React.ReactNode {
    return <View style={styles.topSection}>{this._renderTitle()}</View>;
  }

  _renderBottomSection(): React.ReactNode {
    if (!this.state.displayedSubText || this.state.displayedSubText.length == 0) {
      return false;
    }

    let fontSize = BOX_HEIGHT * 0.25;
    if (Platform.OS === 'android') {
      fontSize = BOX_HEIGHT * 0.2;
    }

    return (
      <View style={styles.bottomSection}>
        <Label
          style={[styles.bottomText, {fontSize: fontSize}]}
          key={this.state.displayedSubText}
          accessible={false}
        >
          {this.state.displayedSubText}
        </Label>
      </View>
    );
  }

  _renderWideText(): React.ReactNode {
    let subText = false;
    if (this.state.displayedSubText) {
      subText = (
        <Label style={styles.rightText} key={this.state.displayedSubText} accessible={false}>
          {this.state.displayedSubText}
        </Label>
      );
    }

    return (
      <View style={[styles.wideTextContainer, {alignSelf: 'center'}]}>
        <Label style={styles.leftText} key={this.props.primaryText} accessible={false}>
          {this.props.primaryText}
        </Label>
        {subText}
      </View>
    );
  }

  _renderWideContentBox(): React.ReactNode {
    return <View style={styles.wideContentBox}>{this._renderWideText()}</View>;
  }

  _renderHR(): React.ReactNode | null {
    if (
      !this.state.displayedSubText ||
      !this.state.displayedSubText.length ||
      this.props.isExtendedWidth
    ) {
      return null;
    }
    return (
      <View key='HR' pointerEvents={'none'} style={styles.hrContainer}>
        <View key='line' style={[styles.hr]} />
        <View key='lineShadow' style={[styles.hrShadow]} />
      </View>
    );
  }

  _renderBadge(): React.ReactElement {
    if (this.props.isBestOffer) {
      const badgeText = I18n.message('Best Value');
      return (
        <Animatable.View
          animation='zoomIn'
          delay={BADGE_ANIMATION_DELAY}
          duration={BADGE_ANIMATION_DURATION}
          style={[styles.badgeContainer, styles.center]}
        >
          <Label
            style={styles.badgeText}
            accessibilityLabel={badgeText}
            testID={TEST_IDS.DEPOSIT_OFFER_TILE_BEST_VALUE_BAGDE}
          >
            {badgeText}
          </Label>
        </Animatable.View>
      );
    }
  }

  _renderContent(): React.ReactNode | null {
    if (this.props.isExtendedWidth && !this.props.primaryTextLine2) {
      return this._renderWideContentBox();
    }
    return this._renderSmallContentBox();
  }

  _handlePress() {
    this.props?.onChange();
  }

  render(): React.ReactNode {
    let locations = [0, 1];
    let colors = [btnGradientStore[0], btnGradientStore[1]];

    if (
      this.state.displayedSubText &&
      this.state.displayedSubText.length &&
      !this.props.isExtendedWidth
    ) {
      locations = [0, 1];
      colors = [btnGradientStore[0], btnGradientStore[1]];
    }

    let depositLabel = this.props.index;
    if (depositLabel == undefined) {
      depositLabel = 'bonus';
    }

    const opacity = this.props.touchOpacity ? this.props.touchOpacity : 0.4;
    return (
      <Animatable.View
        ref={(ref) => (this.depositButton = ref)}
        style={[styles.depositBox, this.props.style]}
        useNativeDriver={Platform.OS === 'ios'}
        accessible={true}
        accessibilityLabel={'deposit_offer_' + depositLabel}
      >
        <TouchableOpacity
          activeOpacity={opacity}
          style={{flexGrow: 1}}
          onPress={this._handlePress.bind(this)}
        >
          <View style={{alignSelf: 'stretch', alignItems: 'stretch', flexGrow: 1}}>
            <LinearGradient style={[styles.gradient]} colors={colors} locations={locations} />
            <View key={'content'} pointerEvents={'none'} style={[styles.contentBox, {flexGrow: 1}]}>
              <SheenEffect
                style={{flexGrow: 1, marginTop: 1, marginBottom: 1, flex: 1}}
                showEffect={this.props.isBestOffer}
              >
                {this._renderContent()}
                {this._renderCheck()}
              </SheenEffect>
            </View>
          </View>
          {this._renderBadge()}
        </TouchableOpacity>
      </Animatable.View>
    );
  }
}
